<script>
import moment from 'moment/moment';
import { mapState } from 'vuex';
import WarningModal from '@/components/modals/WarningModal.vue';
import AddAdditionalVisitorsInCell from '@/components/inCellVisits/AddAdditionalVisitorsInCell.vue';
import AdditionalVisitorDetails from '@/components/additionalVisitors/details/AdditionalVisitorDetails.vue';
import InCellVisitsService from '@/services/InCellVisitsService';

export default {
  name: 'InCellAdditionalVisitors',
  components: {
    WarningModal,
    AddAdditionalVisitorsInCell,
    AdditionalVisitorDetails,
  },
  props: {
    showAddBtn: {
      type: Boolean,
      default: true,
    },
    showRemoveBtn: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      perPage: 0,
      currentPage: 1,
      headers: [
        { key: 'fullName', label: 'Name' },
        { key: 'birthDateAndAge', label: 'Date of Birth' },
        { key: 'status', label: 'Verification status' },
        { key: 'remove', label: '', thStyle: { width: '5%' } },
      ],
      activeParticipant: null,
      loading: false,
      addMode: false,
    };
  },
  computed: {
    ...mapState({
      visit: state => state.inCellVisits.visit,
      rawAdditionalVisitors: state => state.inCellVisits.additionalVisitors,
    }),
    additionalVisitors() {
      return this.rawAdditionalVisitors.map(visitor => ({
        ...visitor,
        fullName: `${visitor.firstname} ${visitor.lastname}`,
        birthDateAndAge: this.formatDobAndAge(visitor.dob),
      }));
    },
  },
  mounted() {

  },
  methods: {
    formatDobAndAge(dob) {
      return `${moment(dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(dob, 'years')})`;
    },
    showModal(name, value, person = null) {
      this.addMode = false;
      this.activeParticipant = person;
      if (value) {
        this.$bvModal.show(name);
      } else {
        this.$bvModal.hide(name);
      }
    },
    closeAllModalAndRefresh() {
      this.$bvModal.hide('modal-remove');
      this.$bvModal.hide('modal-view-participant');
      this.$emit('refresh');
    },
    handleRemove(rejectionReason) {
      this.removeFromVisit(this.activeParticipant, rejectionReason);
    },
    removeFromVisit(participant, rejectionReason) {
      this.loading = true;
      InCellVisitsService.removeAdditionalVisitor(
        this.visit._id,
        participant.additional_visitor_id,
        rejectionReason,
      ).then(() => {
        this.$toast.success(`${participant.firstname} removed from visit`, {
          toastClassName: ['toast-std', 'success-toast'],
        });
      }).catch(() => {
        this.$toast.error('Could not remove participant from visit, please refresh and try again', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }).finally(() => {
        this.loading = false;
        this.closeAllModalAndRefresh();
      })
    },
    getVerificationStatus(status) {
      return status === 'AwaitingVerification' ? 'Awaiting Verification' : status;
    },
    viewParticipantDetails(item) {
      this.showModal('modal-view-participant', true, item)
    },
    rowClass(item) {
      if (item && item.verification_status !== 'Verified') {
        return 'table-danger';
      }
      return '';
    },
  },
};
</script>

<template>
  <b-card>
    <b-card-title class="d-flex align-items-center">
      Additional Participants

      <div class="ui-spacer" />
      <b-btn v-if="showAddBtn" size="sm" variant="primary" @click="addMode = !addMode">
        <span class="fa fa-plus" /> Add
      </b-btn>
    </b-card-title>

    <section v-if="additionalVisitors.length">
      <b-table
        class="data-table"
        hover
        :items="additionalVisitors"
        :fields="headers"
        :per-page="perPage"
        :responsive="true"
        :current-page="currentPage"
        :tbody-tr-class="rowClass"
        @row-clicked="viewParticipantDetails"
      >
        <template v-if="showRemoveBtn" #cell(remove)="data">
          <b-btn size="sm" variant="danger" @click="showModal('modal-remove', true, data.item)"><span
            class="fa fa-minus"
          /></b-btn>
        </template>

        <template #cell(birthDateAndAge)="data">
          <span class="d-flex flex-column" style="width: 115px">{{ data.item.birthDateAndAge }}</span>
          <span v-if="data.item.under18 && !data.item.safeguarded_at_prison" class="badge badge-danger badge-pill">UNDER 18</span>
          <span v-else-if="data.item.under18 && data.item.safeguarded_at_prison" class="badge badge-success badge-pill">UNDER 18</span>
        </template>

        <template #cell(status)="data">
          {{ getVerificationStatus(data.item.verification_status) }}
        </template>
      </b-table>
    </section>

    <p v-if="!additionalVisitors.length && !addMode">Visit has no Additional Participants.</p>

    <section v-if="addMode" class="mt-2">
      <add-additional-visitors-in-cell @view="showModal('modal-view-participant', true, $event)" />
    </section>

    <b-modal id="modal-remove" hide-footer>
      <warning-modal
        v-if="activeParticipant" key="remove-warning"
        :subtitle="activeParticipant.firstname + ' ' + activeParticipant.lastname + '?'"
        show-text-field
        text-field-label="Rejection reason (optional)"
        title="Are you sure you want to remove participant"
        @close="closeAllModalAndRefresh()"
        @ok="handleRemove"
      />
    </b-modal>

    <b-modal id="modal-view-participant" size="lg" hide-footer>
      <additional-visitor-details
        v-if="activeParticipant"
        :additional_visitor_id="activeParticipant.additional_visitor_id"
        @close="closeAllModalAndRefresh()"
      />
    </b-modal>
  </b-card>
</template>

<style scoped>
.table-danger {
  background-color: #f8d7da !important;
}
</style>
